/**
 * Topic lexicon for the unified filter architecture.
 *
 * Data only: the classification *engine* lives in `topicFilter.ts`, so adding a
 * topic is a new entry here (id, label, terms, hashtag aliases) and nothing else.
 *
 * Term weights encode how much a word alone says about the subject, which is what
 * keeps a passing mention from becoming a classification:
 *
 * - **strong** (3): nouns that are unambiguous inside their topic
 *   (`bitcoin`, `touchdown`, `curriculum`);
 * - **medium** (2): clearly indicative but not conclusive (`football`, `election`);
 * - **weak** (1): ambiguous on their own (`game`, `team`, `price`, `match`).
 *
 * A term ending in `*` is a prefix (`politic*` matches politics, political,
 * politician). Terms may be phrases (`world cup`) and may be written in any
 * script; Latin terms are matched on word boundaries, other scripts as substrings
 * (which also covers inflected forms).
 *
 * Proper nouns of individual people are deliberately absent: mentioning a
 * politician, an athlete or a company is not the same as discussing politics,
 * sports or business.
 */
export const TOPICS = [
  "sports",
  "politics",
  "art",
  "technology",
  "business",
  "education",
  "health",
  "music",
  "entertainment",
  "science",
  "world-news",
  "gaming",
  "food",
  "travel",
  "environment",
  "finance",
  "lifestyle",
  "fashion",
  "history",
  "religion",
  "cryptocurrency",
  "programming",
  "design",
  "photography",
  "movies-tv",
  "literature",
  "relationships",
  "personal-development",
  "other",
] as const

export type TopicId = (typeof TOPICS)[number]

/** English labels; the UI translates them with `t(label)`. */
export const TOPIC_LABELS: Record<TopicId, string> = {
  sports: "Sports",
  politics: "Politics",
  art: "Art",
  technology: "Technology",
  business: "Business",
  education: "Education",
  health: "Health",
  music: "Music",
  entertainment: "Entertainment",
  science: "Science",
  "world-news": "World News",
  gaming: "Gaming",
  food: "Food",
  travel: "Travel",
  environment: "Environment",
  finance: "Finance",
  lifestyle: "Lifestyle",
  fashion: "Fashion",
  history: "History",
  religion: "Religion",
  cryptocurrency: "Cryptocurrency",
  programming: "Programming",
  design: "Design",
  photography: "Photography",
  "movies-tv": "Movies & TV",
  literature: "Literature",
  relationships: "Relationships",
  "personal-development": "Personal Development",
  other: "Other",
}

export interface TopicLexicon {
  strong?: string[]
  medium?: string[]
  weak?: string[]
}

/**
 * `other` has no lexicon on purpose: it is only assigned when the author tags a
 * post as other / misc / general, never by guessing.
 */
export const TOPIC_LEXICON: Record<Exclude<TopicId, "other">, TopicLexicon> = {
  sports: {
    strong: [
      "world cup",
      "premier league",
      "champions league",
      "super bowl",
      "world series",
      "nba",
      "nfl",
      "fifa",
      "uefa",
      "olympic*",
      "touchdown",
      "goalkeeper",
      "playoff*",
      "hat-trick",
      "grand slam",
      "wicket",
      "marathon",
      "fútbol",
      "futebol",
      "футбол",
      "サッカー",
      "足球",
      "篮球",
    ],
    medium: [
      "football",
      "soccer",
      "basketball",
      "baseball",
      "tennis",
      "cricket",
      "hockey",
      "boxing",
      "ufc",
      "rugby",
      "cycling",
      "athlet*",
      "tournament",
      "championship",
      "league",
      "referee",
      "selección",
      "équipe",
      "mannschaft",
      "команда",
      "野球",
    ],
    weak: [
      "game",
      "match",
      "team",
      "player",
      "score",
      "goal",
      "season",
      "stadium",
      "fan*",
      "partido",
      "spiel",
      "матч",
      "比赛",
      "試合",
    ],
  },
  politics: {
    strong: [
      "election",
      "ballot",
      "parliament",
      "senate",
      "congress",
      "legislat*",
      "referendum",
      "democracy",
      "impeachment",
      "coalition government",
      "выборы",
      "选举",
    ],
    medium: [
      "politic*",
      "government",
      "minister",
      "policy",
      "diplomat*",
      "sanction*",
      "campaign",
      "voter*",
      "authoritarian",
      "constitution",
      "cabinet",
      "politik",
      "política",
      "politique",
      "gobierno",
      "gouvernement",
      "regierung",
      "правительство",
      "политика",
      "政治",
      "政府",
    ],
    weak: [
      "vote",
      "party",
      "senator",
      "governor",
      "mayor",
      "law",
      "bill",
      "protest",
      "elecciones",
      "élection",
      "wahl",
    ],
  },
  art: {
    strong: ["sculpture", "exhibition", "renaissance", "watercolor", "oil on canvas", "mural", "gallery opening"],
    medium: [
      "art",
      "artist",
      "artwork",
      "painting",
      "drawing",
      "sketch",
      "illustration",
      "curator",
      "museum",
      "portrait",
      "arte",
      "pintura",
      "peinture",
      "kunst",
      "gemälde",
      "искусство",
      "картина",
      "艺术",
      "绘画",
    ],
    weak: ["canvas", "brush", "gallery", "aesthetic"],
  },
  technology: {
    strong: [
      "smartphone",
      "silicon valley",
      "semiconductor",
      "data center",
      "cloud computing",
      "neural network",
      "artificial intelligence",
      "operating system",
      "machine learning",
      "large language model",
      "технология",
      "技术",
    ],
    medium: [
      "tech",
      "technology",
      "software",
      "hardware",
      "gadget",
      "robot*",
      "algorithm",
      "browser",
      "laptop",
      "chip",
      "technologie",
      "технологии",
      "科技",
      "スマホ",
    ],
    weak: ["app", "device", "internet", "platform", "computer", "phone", "digital", "ai", "server", "update"],
  },
  business: {
    strong: [
      "acquisition",
      "merger",
      "venture capital",
      "series a",
      "supply chain",
      "quarterly results",
      "stakeholder*",
      "franchise",
    ],
    medium: [
      "company",
      "startup",
      "business",
      "enterprise",
      "revenue",
      "ceo",
      "founder*",
      "hiring",
      "b2b",
      "saas",
      "empresa",
      "entreprise",
      "unternehmen",
      "бизнес",
      "商业",
      "企业",
    ],
    weak: ["office", "customer*", "employee*", "sales", "deal", "growth", "product", "launch*"],
  },
  education: {
    strong: [
      "curriculum",
      "university",
      "scholarship",
      "student loan",
      "thesis",
      "dissertation",
      "pedagog*",
      "classroom",
    ],
    medium: [
      "education",
      "school",
      "teacher*",
      "professor",
      "lecture",
      "course*",
      "lesson*",
      "degree",
      "campus",
      "syllabus",
      "educación",
      "éducation",
      "bildung",
      "образование",
      "教育",
      "学校",
    ],
    weak: ["student*", "class", "college", "study*", "learn*", "homework", "training"],
  },
  health: {
    strong: [
      "vaccine",
      "diagnosis",
      "clinical trial",
      "mental health",
      "public health",
      "surgery",
      "antibiotic*",
      "diabetes",
      "cancer",
      "nutrition",
      "symptom*",
    ],
    medium: [
      "health",
      "medical",
      "doctor*",
      "nurse*",
      "patient*",
      "disease*",
      "treatment*",
      "therapy",
      "medication",
      "immune",
      "virus",
      "pandemic",
      "salud",
      "santé",
      "gesundheit",
      "здоровье",
      "健康",
      "医疗",
    ],
    weak: ["diet*", "exercise", "workout", "wellness", "fitness", "calorie*", "injury", "médico", "arzt", "медицина"],
  },
  music: {
    strong: [
      "album",
      "spotify",
      "lyrics",
      "guitar",
      "piano",
      "concert",
      "vinyl",
      "playlist",
      "rapper",
      "orchestra",
      "dj set",
    ],
    medium: [
      "music",
      "musician*",
      "singer",
      "song*",
      "band",
      "jazz",
      "hip-hop",
      "hiphop",
      "techno",
      "melody",
      "música",
      "musique",
      "musik",
      "музыка",
      "音楽",
      "音乐",
      "歌曲",
    ],
    weak: ["track", "listen", "sound", "tour", "beat*", "canción", "chanson", "konzert", "песня", "ライブ"],
  },
  entertainment: {
    strong: [
      "netflix",
      "celebrity",
      "red carpet",
      "reality show",
      "talk show",
      "tiktok",
      "influencer",
      "oscars",
      "grammy*",
    ],
    medium: ["entertainment", "hollywood", "viral", "youtube", "podcast", "unterhaltung", "развлечение", "娱乐"],
    weak: ["show", "star", "famous", "watch", "actor", "actress", "drama", "celebridade"],
  },
  science: {
    strong: [
      "research paper",
      "peer review",
      "quantum",
      "astronomy",
      "telescope",
      "climate model",
      "genome",
      "neuroscience",
      "preprint",
      "arxiv",
      "spacecraft",
      "mars rover",
      "particle physics",
    ],
    medium: [
      "science",
      "scientist*",
      "experiment*",
      "hypothesis",
      "laboratory",
      "molecule",
      "physics",
      "chemistry",
      "biology",
      "ciencia",
      "wissenschaft",
      "наука",
      "科学",
      "研究",
    ],
    weak: ["research", "study", "theory", "evidence", "discovery", "dataset", "recherche", "исследование"],
  },
  "world-news": {
    strong: [
      "breaking news",
      "ceasefire",
      "earthquake",
      "hurricane",
      "united nations",
      "nato",
      "coup",
      "humanitarian",
      "airstrike",
      "refugee*",
    ],
    medium: [
      "conflict",
      "invasion",
      "troops",
      "disaster",
      "flood*",
      "wildfire*",
      "war in",
      "noticias",
      "actualités",
      "nachrichten",
      "новости",
      "新闻",
      "战争",
    ],
    weak: ["news", "world", "crisis", "military", "border", "protest*", "attack", "report*", "guerra", "krieg", "война"],
  },
  gaming: {
    strong: [
      "playstation",
      "xbox",
      "nintendo",
      "steam deck",
      "valorant",
      "minecraft",
      "fortnite",
      "elden ring",
      "speedrun*",
      "esports",
      "game pass",
      "boss fight",
    ],
    medium: [
      "gaming",
      "gamer*",
      "video game*",
      "gameplay",
      "console*",
      "quest",
      "rpg",
      "fps",
      "patch notes",
      "videospiel*",
      "videojuego*",
      "ゲーム",
      "游戏",
    ],
    weak: ["game", "player", "level", "loot", "joueur", "jugador"],
  },
  food: {
    strong: [
      "recipe",
      "restaurant",
      "sourdough",
      "espresso",
      "ramen",
      "barbecue",
      "brunch",
      "michelin",
      "food truck",
      "vegan",
      "dessert",
    ],
    medium: [
      "cooking",
      "baking",
      "ingredient*",
      "chef",
      "dish*",
      "meal*",
      "flavor",
      "kitchen",
      "receta",
      "recette",
      "cuisine",
      "rezept",
      "küche",
      "рецепт",
      "料理",
      "レシピ",
      "美食",
      "食谱",
    ],
    weak: ["food", "eat", "dinner", "lunch", "breakfast", "taste", "comida", "essen", "еда"],
  },
  travel: {
    strong: [
      "itinerary",
      "backpacking",
      "road trip",
      "boarding pass",
      "layover",
      "hostel",
      "sightseeing",
      "passport control",
      "airbnb",
    ],
    medium: [
      "travel*",
      "tourism",
      "tourist*",
      "destination*",
      "airport",
      "airline*",
      "flight",
      "hotel",
      "hiking",
      "camping",
      "viaje",
      "voyage",
      "reise",
      "путешествие",
      "旅行",
      "旅游",
      "酒店",
    ],
    weak: ["trip", "journey", "vacation", "holiday", "tour", "beach*", "vuelo", "flug", "поездка", "ホテル"],
  },
  environment: {
    strong: [
      "climate change",
      "carbon emission*",
      "biodiversity",
      "deforestation",
      "renewable energy",
      "solar panel*",
      "wind farm",
      "fossil fuel*",
      "global warming",
      "extinction",
      "recycling",
    ],
    medium: [
      "climate",
      "environment*",
      "emission*",
      "pollution",
      "ecosystem",
      "sustainability",
      "drought",
      "medio ambiente",
      "cambio climático",
      "environnement",
      "umwelt",
      "klima",
      "экология",
      "环境",
      "气候",
    ],
    weak: ["green", "nature", "planet", "co2", "conservation", "climat"],
  },
  finance: {
    strong: [
      "stock market",
      "wall street",
      "interest rate",
      "bond yield",
      "dividend",
      "hedge fund",
      "recession",
      "gdp",
      "central bank",
      "earnings report",
    ],
    medium: [
      "stock*",
      "investor*",
      "investing",
      "portfolio",
      "valuation",
      "inflation",
      "mortgage",
      "asset*",
      "bolsa",
      "inflación",
      "акции",
      "инфляция",
      "股市",
      "金融",
    ],
    weak: ["market*", "money", "profit", "trader*", "loan", "tax*", "budget", "economy", "finanzas", "finanzen"],
  },
  cryptocurrency: {
    strong: [
      "bitcoin",
      "ethereum",
      "blockchain",
      "satoshi",
      "lightning network",
      "web3",
      "defi",
      "nft",
      "altcoin",
      "memecoin",
      "solana",
      "dogecoin",
      "stablecoin",
      "halving",
    ],
    medium: ["crypto", "onchain", "on-chain", "sats", "wallet", "биткоин", "比特币", "区块链"],
    weak: ["token", "coin", "mining", "hodl", "криптовалюта", "criptomoneda"],
  },
  programming: {
    strong: [
      "typescript",
      "javascript",
      "python",
      "golang",
      "refactor*",
      "pull request",
      "unit test*",
      "compiler",
      "kubernetes",
      "docker",
      "repository",
      "npm",
    ],
    medium: [
      "code",
      "coding",
      "programmer*",
      "developer*",
      "backend",
      "frontend",
      "database",
      "framework",
      "deploy*",
      "debug*",
      "syntax",
      "rust",
      "react.js",
      "vue.js",
      "svelte",
      "git",
    ],
    weak: ["function*", "library", "api", "build", "types", "script"],
  },
  design: {
    strong: [
      "ux",
      "ui design",
      "figma",
      "typography",
      "wireframe*",
      "design system",
      "brand identity",
      "user research",
      "usability",
      "color palette",
    ],
    medium: ["design", "designer*", "prototype*", "layout", "accessibility", "interface", "visual identity"],
    weak: ["clean", "simple", "grid", "spacing"],
  },
  photography: {
    strong: [
      "aperture",
      "shutter speed",
      "mirrorless",
      "35mm",
      "golden hour",
      "lightroom",
      "bokeh",
      "telephoto",
      "tripod",
      "raw file",
    ],
    medium: [
      "photo*",
      "photograph*",
      "camera",
      "exposure",
      "fotografía",
      "photographie",
      "fotografie",
      "фотография",
      "写真",
      "摄影",
    ],
    weak: ["shoot", "shot", "lens", "frame", "focus", "cámara", "kamera"],
  },
  "movies-tv": {
    strong: [
      "season finale",
      "film festival",
      "cinematograph*",
      "screenplay",
      "box office",
      "k-drama",
      "sitcom",
      "documentary",
      "trailer",
    ],
    medium: [
      "movie",
      "film",
      "tv show",
      "anime",
      "netflix",
      "hbo",
      "streaming",
      "película",
      "série",
      "cinéma",
      "фильм",
      "сериал",
      "映画",
      "ドラマ",
      "电影",
      "电视剧",
    ],
    weak: ["episode", "series", "cast", "plot", "spoiler*", "scene", "character", "actor", "actress", "cine"],
  },
  literature: {
    strong: [
      "novel",
      "poem",
      "poetry",
      "short story",
      "manuscript",
      "book launch",
      "prose",
      "memoir",
      "anthology",
      "book club",
      "literary",
    ],
    medium: [
      "book*",
      "writer",
      "fiction",
      "nonfiction",
      "essay",
      "narrative",
      "publisher",
      "novela",
      "buch",
      "книга",
      "роман",
      "小説",
      "小说",
    ],
    weak: ["author", "reading", "story", "write", "writing", "chapter", "libro", "livre", "poesía", "poésie"],
  },
  relationships: {
    strong: [
      "dating app",
      "relationship advice",
      "breakup",
      "divorce",
      "long distance",
      "attachment style",
      "love language",
      "red flag*",
    ],
    medium: [
      "relationship*",
      "dating",
      "couple*",
      "marriage",
      "friendship",
      "partnership",
      "romance",
      "relación",
      "pareja",
      "beziehung",
      "отношения",
      "恋爱",
      "关系",
    ],
    weak: ["partner*", "family", "parent*", "trust", "love", "friend*", "amor", "liebe", "любовь"],
  },
  "personal-development": {
    strong: [
      "self improvement",
      "growth mindset",
      "goal setting",
      "atomic habits",
      "time management",
      "self-help",
      "personal growth",
      "morning routine",
      "deep work",
    ],
    medium: [
      "productivity",
      "discipline",
      "motivation",
      "mindset",
      "meditation",
      "stoic*",
      "journaling",
      "habit*",
      "resilience",
      "crecimiento personal",
      "développement personnel",
      "саморазвитие",
      "自我提升",
    ],
    weak: ["focus", "consistency", "reflection", "confidence", "improve", "better", "hábitos"],
  },
  lifestyle: {
    strong: [
      "morning routine",
      "self care",
      "declutter*",
      "minimalism",
      "slow living",
      "work life balance",
      "home decor",
    ],
    medium: [
      "lifestyle",
      "routine",
      "wellbeing",
      "mindful*",
      "estilo de vida",
      "mode de vie",
      "lebensstil",
      "образ жизни",
      "生活方式",
      "生活",
    ],
    weak: ["life", "daily", "tips", "vlog", "habit*", "organization"],
  },
  fashion: {
    strong: ["haute couture", "fashion week", "streetwear", "runway", "outfit", "sneaker*", "boutique", "wardrobe"],
    medium: ["fashion", "apparel", "textile", "makeup", "cosmetic*", "skincare", "moda", "mode", "мода", "时尚", "穿搭"],
    weak: ["style", "designer", "collection", "wear", "look", "trend", "dress", "shoes", "ropa", "kleidung", "одежда"],
  },
  history: {
    strong: [
      "world war",
      "cold war",
      "civil war",
      "empire",
      "dynasty",
      "archaeolog*",
      "medieval",
      "ancient rome",
      "artifact*",
      "colonial*",
      "anniversary of",
    ],
    medium: [
      "history",
      "historic*",
      "historian",
      "archive*",
      "manuscript",
      "excavation",
      "revolution",
      "historia",
      "histoire",
      "geschichte",
      "история",
      "历史",
    ],
    weak: ["century", "past", "era", "ancient"],
  },
  religion: {
    strong: [
      "bible",
      "quran",
      "torah",
      "gospel",
      "sermon",
      "theolog*",
      "ramadan",
      "mosque",
      "synagogue",
      "church service",
      "prayer",
    ],
    medium: [
      "religion",
      "religious",
      "christian*",
      "muslim*",
      "buddhis*",
      "hindu*",
      "jewish",
      "islam",
      "spiritual*",
      "worship",
      "scripture",
      "religión",
      "церковь",
      "宗教",
      "信仰",
    ],
    weak: ["faith", "god", "pray", "church", "temple", "belief", "soul", "holy", "iglesia", "kirche", "религия"],
  },
}

/**
 * Hashtags, NIP-32 labels and custom tags that map straight to a topic.
 *
 * Keys are normalised with `normalizeTopicToken`, so casing, `#`, spaces and
 * punctuation do not matter.
 */
export const TOPIC_ALIASES: Record<string, TopicId> = {
  sport: "sports",
  sports: "sports",
  futbol: "sports",
  futebol: "sports",
  nba: "sports",
  nfl: "sports",
  mlb: "sports",
  fifa: "sports",
  uefa: "sports",
  formula1: "sports",
  f1: "sports",
  politics: "politics",
  politica: "politics",
  politique: "politics",
  politik: "politics",
  tech: "technology",
  technology: "technology",
  ai: "technology",
  nostr: "technology",
  dev: "programming",
  developers: "programming",
  programming: "programming",
  coding: "programming",
  code: "programming",
  opensource: "programming",
  crypto: "cryptocurrency",
  bitcoincash: "cryptocurrency",
  bitcoin: "cryptocurrency",
  btc: "cryptocurrency",
  ethereum: "cryptocurrency",
  eth: "cryptocurrency",
  blockchain: "cryptocurrency",
  finance: "finance",
  economy: "finance",
  stocks: "finance",
  investing: "finance",
  business: "business",
  art: "art",
  artist: "art",
  design: "design",
  ux: "design",
  ui: "design",
  photo: "photography",
  photography: "photography",
  music: "music",
  news: "world-news",
  worldnews: "world-news",
  movie: "movies-tv",
  movies: "movies-tv",
  film: "movies-tv",
  cinema: "movies-tv",
  book: "literature",
  books: "literature",
  reading: "literature",
  gaming: "gaming",
  games: "gaming",
  esports: "gaming",
  food: "food",
  recipe: "food",
  cooking: "food",
  travel: "travel",
  environment: "environment",
  climate: "environment",
  science: "science",
  space: "science",
  health: "health",
  fitness: "health",
  education: "education",
  history: "history",
  religion: "religion",
  faith: "religion",
  lifestyle: "lifestyle",
  fashion: "fashion",
  relationships: "relationships",
  dating: "relationships",
  selfimprovement: "personal-development",
  personaldevelopment: "personal-development",
  productivity: "personal-development",
  motivation: "personal-development",
  other: "other",
  misc: "other",
  miscellaneous: "other",
  general: "other",
  random: "other",
}

/** Lowercases and strips everything that is not a letter or a digit. */
export function normalizeTopicToken(value: string): string {
  return value
    .toLowerCase()
    .replace(/^#+/, "")
    .replace(/[^\p{L}\p{N}]+/gu, "")
}

const TOPIC_BY_TOKEN = (() => {
  const map = new Map<string, TopicId>()
  for (const id of TOPICS) {
    map.set(normalizeTopicToken(id), id)
    map.set(normalizeTopicToken(TOPIC_LABELS[id]), id)
  }
  for (const [alias, id] of Object.entries(TOPIC_ALIASES)) {
    const token = normalizeTopicToken(alias)
    if (!map.has(token)) map.set(token, id)
  }
  return map
})()

/** Resolves a hashtag, label or tag value to a topic (`#WorldNews` -> world-news). */
export function topicFromToken(value: string | undefined): TopicId | undefined {
  if (!value) return undefined
  const token = normalizeTopicToken(value)
  if (!token) return undefined
  return TOPIC_BY_TOKEN.get(token)
}

/** True for a valid topic id (used to sanitise persisted state). */
export function isTopicId(value: unknown): value is TopicId {
  return typeof value === "string" && (TOPICS as readonly string[]).includes(value)
}

/**
 * Normalised search text per topic: label, id and every hashtag alias, so
 * searching `btc`, `bitcoin` or `crypto` finds Cryptocurrency.
 */
const TOPIC_SEARCH_TEXT = (() => {
  const map = new Map<TopicId, string>()
  for (const id of TOPICS) {
    map.set(id, normalizeTopicToken(`${TOPIC_LABELS[id]} ${id.replace(/-/g, " ")}`))
  }
  for (const [alias, id] of Object.entries(TOPIC_ALIASES)) {
    map.set(id, `${map.get(id) ?? ""}${normalizeTopicToken(alias)}`)
  }
  return map
})()

/**
 * Topics matching a search term (label, id, slug or hashtag alias).
 * An empty term returns every topic.
 */
export function searchTopics(term: string): TopicId[] {
  const needle = normalizeTopicToken(term)
  if (!needle) return [...TOPICS]
  return TOPICS.filter((id) => (TOPIC_SEARCH_TEXT.get(id) ?? "").includes(needle))
}


